import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle, useContext, useEffect } from 'react';
import PropTypes from 'prop-types'
import { getUA } from 'react-device-detect'
import { useSelector } from 'react-redux'
import visitorInfo from 'visitor-info'
/* --------------------------------------------- */
import PlayerSwitcher from './PlayerSwitcher'
import PlayerUI from './PlayerUI';
import styled from 'styled-components'
/* --------------------------------------------- */
import utils from '../../utils/utils'
import useErrorUtils from '../../utils/useErrorUtils'
import { getSelectedPlayerQuality, getSelectedSubtitleTrack } from '../../utils/localStorageService'
// import { useEffectDebugger } from '../../utils/debugger'
/* --------------------------------------------- */
import { PlayerContext } from '../../context/player.context'
/* --------------------------------------------- */
import useLoggly from '../../containers/useLoggly'
import _utils from '../../utils/utils';
/* --------------------------------------------- */
const { browser } = visitorInfo()
/* --------------------------------------------- */
// Container component to handle player events 
const Player = forwardRef(({
    thisIsChannel, defaultPlayer, smilUrl, title, subtitle, isLivePlayer,
    onErrorOkClick, castConfigPlaybackFormat, isPortraitPlayerEnabled, bingeCountdownDuration,
    onPlay, onPause, onCurrentTimeChange, onComplete, onUnload, onBuffer, onSubtitleChange, baseUrl,
    thumbnailImgs, messages, mediaId, uid, seriesId, type, episodeNum, seasonNumber, seriesTitle,
    onConcurrencyLimitViolation, onGeoLocationBlocked, onPlayerVpnBlocked, onLicenseNotGranted, onUnknowFormat, onAccessDenied, onCrash,
    showPlaceholderPlayBtn, onPlaceholderPlayBtnClick, allowCasting,
    qualityMappingMode, qualityMapping, abrMode, debugModeEnabled, isTrailer, isPlayerLoaded, setPlayerLoaded, assetDetailsForRelatedRailInPlayer, catalogTheme
}, ref) => {
    const [playbackAssetFormat, updatePlaybackAssetFormat] = useState('')
    const [playbackPreviewAssetFormat, updatePlaybackPreviewAssetFormat] = useState('')
    const [theme, updateTheme] = useState({})
    const [isShakaPlayerMount, setShakaPlayerMount] = useState(false)
    /* --------------------------------------------- */
    const { dispatch, allPlayerStats } = useContext(PlayerContext)
    const { appMessages } = useSelector(store => store.appConfigReducers)
    /* --------------------------------------------- */
    const playerSwitcherRef = useRef({})
    const playerUIRef = useRef({})
    const isAutoPlay = useRef(false)
    const allPlayerStatsRef = useRef(false)
    const playerElementRef = useRef(null)
    const isTrailerRef = useRef(isTrailer)
    const portraitPlayerRef = useRef(isPortraitPlayerEnabled)
    const isPlayerLoadedRef = useRef(isPlayerLoaded)
    /* --------------------------------------------- */
    const { showError } = useErrorUtils()
    const { log } = useLoggly()
    /* --------------------------------------------- */
    useEffect(() => {
        portraitPlayerRef.current = isPortraitPlayerEnabled
    }, [isPortraitPlayerEnabled])
    /* --------------------------------------------- */
    useEffect(() => {
        isPlayerLoadedRef.current = isPlayerLoaded
    }, [isPlayerLoaded])
    /* --------------------------------------------- */
    useEffect(() => {
        if (!isPlayerLoaded && playerUIRef.current) {
            playerUIRef.current.setPlayerError(false)
            playerUIRef.current.setAutoPlay(false)
        }
    }, [isPlayerLoaded])
    /* --------------------------------------------- */
    /**
     * It: Gets duration.
     */
    const getDuration = useCallback(() => {
        if (playerSwitcherRef && playerSwitcherRef.current && playerSwitcherRef.current.getDuration) return parseInt(playerSwitcherRef.current.getDuration() * 1000)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Sets playing state on play event.
     */
    const _onPlay = useCallback(() => {
        if (playerUIRef.current.updateIsPlaying) playerUIRef.current.updateIsPlaying(true)
        onPlay()
    }, [onPlay])
    /* --------------------------------------------- */
    /**
     * It: Sets paused state on pause event.
     */
    const _onPause = useCallback(() => {
        if (playerUIRef.current.updateIsPlaying) playerUIRef.current.updateIsPlaying(false)
        onPause()
    }, [onPause])
    /* --------------------------------------------- */
    /**
     * It: Sets buffering state on buffer event.
     */
    const _onBuffer = useCallback(() => {
        if (playerUIRef.current.updateIsPlayerLoading) playerUIRef.current.updateIsPlayerLoading(true)
        onBuffer()
    }, [onBuffer])
    /* --------------------------------------------- */
    // const onSubtitleTextChange = useCallback((currentText) => {
    //     if (playerUIRef.current.updateSubTitleText) playerUIRef.current.updateSubTitleText(currentText)
    // }, [])
    /* --------------------------------------------- */
    /**
     * It: Updates loading state on buffer complete.
     */
    const onBufferComplete = useCallback(() => {
        if (playerUIRef.current.updateIsPlayerLoading) playerUIRef.current.updateIsPlayerLoading(false)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Shows concurrency error.
     */
    const _onConcurrencyLimitViolation = useCallback((errorInfo) => {
        if (playerSwitcherRef.current) playerSwitcherRef.current.setPlayerError(true)
        if (playerUIRef.current) playerUIRef.current.setPlayerError(1)
        showError({
            type: 'PlaybackConcurrency',
            value: {
                onPrimaryBtnClick: onErrorOkClick,
                isPlayerError: true,
                isPortraitPlayer: portraitPlayerRef.current
            },
            errorInfo
        })
        onConcurrencyLimitViolation(errorInfo)
    }, [
        showError,
        onErrorOkClick,
        onConcurrencyLimitViolation
    ])
    /* --------------------------------------------- */
    /**
     * It: Shows geo restricted error.
     */
    const _onGeoLocationBlocked = useCallback((errorInfo) => {
        if (playerSwitcherRef.current) playerSwitcherRef.current.setPlayerError(true)
        if (playerUIRef.current) playerUIRef.current.setPlayerError(2)
        showError({
            type: 'PlaybackGeoRestricted',
            value: {
                onPrimaryBtnClick: onErrorOkClick,
                isPlayerError: true,
                isPortraitPlayer: portraitPlayerRef.current
            },
            errorInfo
        })
        onGeoLocationBlocked(errorInfo)
    }, [
        showError,
        onErrorOkClick,
        onGeoLocationBlocked
    ])
    /* --------------------------------------------- */
    /**
     * It: Shows VPN blocked error.
     */
    const _onPlayerVpnBlocked = useCallback((errorInfo) => {
        if (playerSwitcherRef.current) playerSwitcherRef.current.setPlayerError(true)
        if (playerUIRef.current) playerUIRef.current.setPlayerError(2)
        showError({
            type: 'PlayerVpnBlocked',
            value: {
                onPrimaryBtnClick: onErrorOkClick,
                isPlayerError: true,
                isPortraitPlayer: portraitPlayerRef.current
            },
            errorInfo
        })
        onPlayerVpnBlocked(errorInfo)
    }, [
        showError,
        onErrorOkClick,
        onPlayerVpnBlocked
    ])
    /* --------------------------------------------- */
    /**
     * It: Shows entitlement error.
     */
    const _onAccessDenied = useCallback((errorInfo) => {
        if (playerSwitcherRef.current) playerSwitcherRef.current.setPlayerError(true)
        if (playerUIRef.current) playerUIRef.current.setPlayerError(3)
        showError({
            type: 'PlaybackEntitlement',
            value: {
                onPrimaryBtnClick: onErrorOkClick,
                isPlayerError: true,
                isPortraitPlayer: portraitPlayerRef.current
            },
            errorInfo
        })
        onAccessDenied(errorInfo)
    }, [
        showError,
        onErrorOkClick,
        onAccessDenied
    ])
    /* --------------------------------------------- */
    /**
     * It: Shows generic error.
     */
    const _onCrash = useCallback(({ errorCode }, errorInfo) => {

        let shakaErrorLog = errorInfo?.shakaErrorLog ? JSON.parse(errorInfo?.shakaErrorLog) : {};
        let errorType = '';
        if (shakaErrorLog?.category === 6 || errorCode === 6001) {
            if (playerSwitcherRef.current) playerSwitcherRef.current.setPlayerError(true)
            if (playerUIRef.current) playerUIRef.current.setPlayerError(4)
            errorType = 'PlaybackDRM';
            showError({
                type: 'PlaybackDRM',
                value: {
                    onPrimaryBtnClick: onErrorOkClick,
                    isPlayerError: true,
                    isPortraitPlayer: portraitPlayerRef.current
                },
                errorInfo
            })
        } else {
            const urlExtension = shakaErrorLog?.data?.[0] ? _utils.getUrlExtension(shakaErrorLog?.data?.[0]): null;
                if (playerSwitcherRef.current) playerSwitcherRef.current.setPlayerError(true)
                if (playerUIRef.current) playerUIRef.current.setPlayerError(0)
                if(shakaErrorLog && (shakaErrorLog?.category === 4 || ['m3u8', 'mpd'].indexOf(urlExtension) > -1)) {
                    errorType = 'ManifestFailed';
                    showError({
                        type: 'ManifestFailed',
                        value: {
                            onPrimaryBtnClick: onErrorOkClick,
                            isPlayerError: true,
                            isPortraitPlayer: portraitPlayerRef.current
                        },
                        errorInfo
                    })
                } else if(['m4a', 'm4v', 'm4i'].indexOf(urlExtension) > -1) {
                    errorType = 'PlayerSegmentFailed'
                    showError({
                        type: 'PlayerSegmentFailed',
                        value: {
                            onPrimaryBtnClick: onErrorOkClick,
                            // isPlayerError: true,
                            isPortraitPlayer: portraitPlayerRef.current
                        },
                        errorInfo
                    })
                } else {
                    errorType = 'PlaybackGeneric'
                    showError({
                        type: 'PlaybackGeneric',
                        value: {
                            onPrimaryBtnClick: onErrorOkClick,
                            isPlayerError: true,
                            isPortraitPlayer: portraitPlayerRef.current
                        },
                        errorInfo
                    })
                }
            
        }
        onCrash(errorInfo, errorType)

    }, [onCrash, showError, onErrorOkClick])
    /* --------------------------------------------- */
    /**
     * It: Shows license not granted error.
     */
    const _onLicenseNotGranted = useCallback((errorInfo) => {
        if (playerSwitcherRef.current) playerSwitcherRef.current.setPlayerError(true)
        if (playerUIRef.current) playerUIRef.current.setPlayerError(3)
        showError({
            type: 'PlaybackEntitlement',
            value: {
                onPrimaryBtnClick: onErrorOkClick,
                isPlayerError: true,
                isPortraitPlayer: portraitPlayerRef.current
            },
            errorInfo
        })
        onLicenseNotGranted(errorInfo)
    }, [
        showError,
        onErrorOkClick,
        onLicenseNotGranted
    ])
    /* --------------------------------------------- */
    /**
     * It: Shows unknown format error.
     */
    const _onUnknowFormat = useCallback((errorInfo) => {
        if (playerSwitcherRef.current) playerSwitcherRef.current.setPlayerError(true)
        if (playerUIRef.current) playerUIRef.current.setPlayerError(6)
        showError({
            type: 'PlaybackFormat',
            value: {
                onPrimaryBtnClick: onErrorOkClick,
                isPlayerError: true,
                isPortraitPlayer: portraitPlayerRef.current
            },
            errorInfo
        })
        onUnknowFormat(errorInfo)
    }, [
        showError,
        onErrorOkClick,
        onUnknowFormat
    ])
    /* --------------------------------------------- */
    /**
     * It: Tigger play event in player.
     * It: Tigger play event in chromecast.
     */
    const onPlayClick = useCallback(() => {
        if (!isPlayerLoadedRef.current) setPlayerLoaded(true)
        else if (playerSwitcherRef && playerSwitcherRef.current && playerSwitcherRef.current.play) playerSwitcherRef.current.play()
        /* --------------------------------------------- */
        const castPlayer = window.castPlayer
        if (utils.isNotNull(castPlayer, 'remotePlayer', 'isPaused') &&
            utils.isNotNull(castPlayer, 'remotePlayerController', 'playOrPause') &&
            castPlayer.remotePlayer.isPaused) {
            castPlayer.remotePlayerController.playOrPause();
        }
    }, [setPlayerLoaded])
    /* --------------------------------------------- */
    /**
     * It: Tigger pause event in player.
     * It: Tigger pause event in chromecast.
     */
    const onPauseClick = useCallback(() => {
        if (playerSwitcherRef && playerSwitcherRef.current && playerSwitcherRef.current.pause) playerSwitcherRef.current.pause()
        /* --------------------------------------------- */
        const castPlayer = window.castPlayer
        if (utils.isNotNull(castPlayer, 'remotePlayer', 'isPaused') &&
            utils.isNotNull(castPlayer, 'remotePlayerController', 'playOrPause') &&
            !castPlayer.remotePlayer.isPaused) {
            castPlayer.remotePlayerController.playOrPause();
        }
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Mutes playback.
     */
    const onMuteClick = useCallback(() => {
        if (playerSwitcherRef && playerSwitcherRef.current && playerSwitcherRef.current.mute) playerSwitcherRef.current.mute()
    }, [])
    /* --------------------------------------------- */
    /**
     * It: UnMutes playback.
     */
    const onUnmuteClick = useCallback(() => {
        if (playerSwitcherRef && playerSwitcherRef.current && playerSwitcherRef.current.unmute) playerSwitcherRef.current.unmute()
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Sets autoPlay state.
     */
    const autoPlay = useCallback((autoPlay) => {
        if (autoPlay) onPlayClick()
        if (playerUIRef.current.setAutoPlay) playerUIRef.current.setAutoPlay(autoPlay)
        isAutoPlay.current = autoPlay
    }, [onPlayClick])
    /* --------------------------------------------- */
    /**
     * It: Gets current time.
     */
    const getCurrentTime = useCallback(() => {
        if (playerSwitcherRef && playerSwitcherRef.current &&  playerSwitcherRef.current.getCurrentTime) return parseInt(playerSwitcherRef.current.getCurrentTime() * 1000)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Sets current time.
     */
    const setCurrentTime = useCallback((currentTime) => {
        if (!currentTime || isNaN(currentTime) || currentTime < 0) currentTime = 0
        if (utils.isNotNull(playerSwitcherRef, "current", "setCurrentTime")) playerSwitcherRef.current.setCurrentTime(currentTime / 1000)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Get all qualities.
     */
    const getAllQualities = useCallback(() => {
        if (playerSwitcherRef.current) return playerSwitcherRef.current.getAllQualities()
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Get current quality.
     */
    const getCurrentQuality = useCallback(() => {
        if (playerSwitcherRef.current) return playerSwitcherRef.current.getCurrentQuality()
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Sets current quality.
     */
    const setCurrentQuality = useCallback((quality, options) => {
        if (playerSwitcherRef.current) return playerSwitcherRef.current.setCurrentQuality(quality, options)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Get all subtitles.
     */
    const getAllSubtitles = useCallback(() => {
        if (playerSwitcherRef.current) return playerSwitcherRef.current.getAllSubtitles()
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Get current subtitle.
     */
    const getCurrentSubtitle = useCallback(() => {
        if (playerSwitcherRef.current) return playerSwitcherRef.current.getCurrentSubtitle()
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Set current subtitles.
     */
    const setCurrentSubtitle = useCallback((subtitle) => {
        if (playerSwitcherRef && playerSwitcherRef.current && playerSwitcherRef.current.setCurrentSubtitle) playerSwitcherRef.current.setCurrentSubtitle(subtitle)
        onSubtitleChange(subtitle)
    }, [onSubtitleChange])
    /* --------------------------------------------- */
    /**
     * It: Get all audios.
     */
    const getAllAudios = useCallback(() => {
        if (playerSwitcherRef.current) return playerSwitcherRef.current.getAllAudios()
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Get current audio.
     */
    const getCurrentAudio = useCallback(() => {
        if (playerSwitcherRef.current) return playerSwitcherRef.current.getCurrentAudio()
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Sets current audio.
     */
    const setCurrentAudio = useCallback((audio) => {
        if (playerSwitcherRef.current) return playerSwitcherRef.current.setCurrentAudio(audio)
    }, [])
    /* --------------------------------------------- */
    /**
     * When: Current time change.
     * When: Duration time change.
     */
    const _onCurrentTimeChange = useCallback(() => {
        const currentTime = getCurrentTime()
        const duration = getDuration()
        if (playerUIRef.current && playerUIRef.current.onCurrentTimeChange && currentTime) playerUIRef.current.onCurrentTimeChange(currentTime)
        if (playerUIRef.current && playerUIRef.current.updateDuration) playerUIRef.current.updateDuration(duration)
        onCurrentTimeChange(currentTime, duration)
    }, [getCurrentTime, getDuration, onCurrentTimeChange])
    /* --------------------------------------------- */
    /**
     * It: Show/hide back button.
     */
    const showBackBtn = useCallback((isShowBackBtn) => {
        if (playerUIRef.current.showBackBtn) return playerUIRef.current.showBackBtn(isShowBackBtn)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide binge.
     */
    const showBingeWatching = useCallback((isShowBingeWatching) => {
        if (playerUIRef.current.showBingeWatching) return playerUIRef.current.showBingeWatching(isShowBingeWatching)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide breadcrumb.
     */
    const showBreadCrumbView = useCallback((isShowBreadCrumbView) => {
        if (playerUIRef.current.showBreadCrumbView) return playerUIRef.current.showBreadCrumbView(isShowBreadCrumbView)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide elapsed time.
     */
    const showElapsedTime = useCallback((isShowElapsedTime) => {
        if (playerUIRef.current.showElapsedTime) return playerUIRef.current.showElapsedTime(isShowElapsedTime)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide forward button.
     */
    const showForwardBtn = useCallback((isShowForwardBtn) => {
        if (playerUIRef.current.showForwardBtn) return playerUIRef.current.showForwardBtn(isShowForwardBtn)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide forward duration.
     */
    const setForwardDuration = useCallback((duration) => {
        if (playerUIRef.current.setForwardDuration) return playerUIRef.current.setForwardDuration(duration)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Set playback asset format.
     */
    const setPlaybackAssetFormat = useCallback((playbackAssetFormat) => {
        updatePlaybackAssetFormat(playbackAssetFormat)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Set playback preview asset format.
     */
    const setPlaybackPreviewAssetFormat = useCallback((previewAssetFormat) => {
        updatePlaybackPreviewAssetFormat(previewAssetFormat)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide mute/unmute button
     */
    const showMuteUnmuteBtn = useCallback((isShowMuteUnmuteBtn) => {
        if (playerUIRef.current.showMuteUnmuteBtn) return playerUIRef.current.showMuteUnmuteBtn(isShowMuteUnmuteBtn)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide play/pause button
     */
    const showPlayPauseBtn = useCallback((isShowPlayPauseBtn) => {
        if (playerUIRef.current.showPlayPauseBtn) return playerUIRef.current.showPlayPauseBtn(isShowPlayPauseBtn)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide qulaity selector button
     */
    const showQualitySelectorBtn = useCallback((isShowQualitySelectorBtn) => {
        if (playerUIRef.current.showQualitySelectorBtn) return playerUIRef.current.showQualitySelectorBtn(isShowQualitySelectorBtn)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide remaining time
     */
    const showRemainingTime = useCallback((isShowRemainingTime) => {
        if (playerUIRef.current.showRemainingTime) return playerUIRef.current.showRemainingTime(isShowRemainingTime)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide rewind button
     */
    const showRewindBtn = useCallback((isShowRewindBtn) => {
        if (playerUIRef.current.showRewindBtn) return playerUIRef.current.showRewindBtn(isShowRewindBtn)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide rewind duration
     */
    const setRewindDuration = useCallback((duration) => {
        if (playerUIRef.current.setRewindDuration) return playerUIRef.current.setRewindDuration(duration)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide seek bar
     */
    const showSeekBar = useCallback((isShowSeekBar) => {
        if (playerUIRef.current.showSeekBar) return playerUIRef.current.showSeekBar(isShowSeekBar)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide seek preview
     */
    const showSeekPreview = useCallback((isShowSeekPreview) => {
        if (playerUIRef.current.showSeekPreview) return playerUIRef.current.showSeekPreview(isShowSeekPreview)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide title
     */
    const showTitle = useCallback((isShowTitle) => {
        if (playerUIRef.current.showTitle) return playerUIRef.current.showTitle(isShowTitle)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide total duration
     */
    const showTotalDuration = useCallback((isShowTotalDuration) => {
        if (playerUIRef.current.showTotalDuration) return playerUIRef.current.showTotalDuration(isShowTotalDuration)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide subtitle selector button
     */
    const showSubTitleSelectorBtn = useCallback((isShowSubtitleSelectorBtn) => {
        if (playerUIRef.current.showSubTitleSelectorBtn) return playerUIRef.current.showSubTitleSelectorBtn(isShowSubtitleSelectorBtn)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Show/hide fullscreen button
     */
    const showFullscreenBtn = useCallback((isShowFullscreenBtn) => {
        if (playerUIRef.current.showFullscreenBtn) return playerUIRef.current.showFullscreenBtn(isShowFullscreenBtn)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Sets player theme
     */
    const setPlayerTheme = useCallback((theme) => {
        updateTheme(theme)
        if (playerUIRef.current.setPlayerTheme) return playerUIRef.current.setPlayerTheme(theme)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: Shows generic error.
     */
    const setPlayerError = useCallback((isPlayerError, errorInfo) => {
        if (playerSwitcherRef && playerSwitcherRef.current && playerSwitcherRef.current.setPlayerError) playerSwitcherRef.current.setPlayerError(isPlayerError)
        if (playerUIRef.current.setPlayerError) return playerUIRef.current.setPlayerError(0)
        showError({
            type: 'PlaybackGeneric',
            value: {
                onPrimaryBtnClick: onErrorOkClick,
                isPlayerError: true,
                isPortraitPlayer: portraitPlayerRef.current
            },
            errorInfo
        })
    }, [showError, onErrorOkClick])
    /* --------------------------------------------- */
    /**
     * It: Sets player loading state.
     */
    const setPlayerLoading = useCallback((isPlayerLoading) => {
        if (playerUIRef.current.updateIsPlayerLoading) return playerUIRef.current.updateIsPlayerLoading(isPlayerLoading)
    }, [])
    /* --------------------------------------------- */
    /**
     * It: sets state: isShakaPlayerMount on player component mount
     */
     const handlePlayerMount = () => {
       setShakaPlayerMount(true)
     }
     /* --------------------------------------------- */
    /**
     * It: Sets player ready state.
     */
    const _onLoad = useCallback(() => {
        const media = {
            duration: getDuration && getDuration()
        }
        /* --------------------------------------------- */
        let playerSelectedQuality = getCurrentQuality()
        /* --------------------------------------------- */
        let localSelectedSubTitle = getSelectedSubtitleTrack()
        if (playerSwitcherRef && playerSwitcherRef.current && playerSwitcherRef.current.setCurrentSubtitle && !isLivePlayer) playerSwitcherRef.current.setCurrentSubtitle(localSelectedSubTitle.language)
        /* --------------------------------------------- */
        if (playerUIRef.current) {
            if (playerUIRef.current.updateIsPlayerReady) playerUIRef.current.updateIsPlayerReady(true)
            if (playerUIRef.current.updateDuration) playerUIRef.current.updateDuration(media.duration)
            if (playerUIRef.current.updateIsReplayScreen) playerUIRef.current.updateIsReplayScreen(false)
            if (playerUIRef.current.updateDisplayCurrentQuality) playerUIRef.current.updateDisplayCurrentQuality(playerSelectedQuality)
        }
        handlePlayerMount()
        // playerFeature.portraitPlayerEnabled
        if (isAutoPlay.current || isPortraitPlayerEnabled) onPlayClick()
        else onPauseClick()
    }, [getDuration, onPauseClick, onPlayClick, getCurrentQuality, isPortraitPlayerEnabled, isLivePlayer])
    /* --------------------------------------------- */
    /**
     * It: Sends stats to logly.
     */
    const _onUnload = useCallback(() => {
        if (debugModeEnabled) {
            const playerStats = allPlayerStatsRef.current
            const stats4Loggly = {}
            let type4Stats = type || ''
            if (isTrailerRef.current) type4Stats = 'trailer'
            else if (type === 'series') type4Stats = 'episode'
            playerStats.map((stats) => {
                stats.label = ((stats.label || '').toLowerCase() || '').replace(/ /g, '_') || ''
                if (stats.label === 'user_token') return null
                stats4Loggly[stats.label] = stats.value
                return null
            })
            stats4Loggly['content_type'] = type4Stats
            stats4Loggly['url'] = stats4Loggly['release_url'] || ''
            log({
                type: 'Debug',
                attr: {
                    event: 'player_stats',
                    module: 'Player',
                    stats: stats4Loggly
                }
            })
        }
        onUnload()
    }, [onUnload, debugModeEnabled, log, type])
    /* --------------------------------------------- */
    /**
     * It: Sets replay screen state.
     */
    const _onComplete = useCallback(() => {
        if (utils.isNotNull(playerUIRef, 'current', 'updateIsReplayScreen')) playerUIRef.current.updateIsReplayScreen(true)
        onComplete()
    }, [onComplete])
    /* --------------------------------------------- */
    useEffect(() => {
        allPlayerStatsRef.current = allPlayerStats
    }, [allPlayerStats])
    /* --------------------------------------------- */
    /**
     * It: Dispatches player stats events.
     */
    useEffect(() => {
        dispatch({ type: "USER_AGENT", value: getUA })
        dispatch({ type: "START_TIME", value: new Date() })
        if (window.shaka) {
            dispatch({ type: 'PLAYER_VERSION', value: window.shaka.Player.version })
        }

        const isSafari = browser.name === 'Safari'
        if (!isSafari) {
            let selectedPlayerQuality = getSelectedPlayerQuality() || ''
            if (!selectedPlayerQuality) selectedPlayerQuality = appMessages.label_player_auto
            dispatch({ type: "STARTING_QUALITY", value: selectedPlayerQuality })
        }
    }, [dispatch, appMessages.label_player_auto])
    /* --------------------------------------------- */
    useImperativeHandle(ref, () => {
        return {
            onPlayClick, onPauseClick, onMuteClick, onUnmuteClick,
            autoPlay, getCurrentTime, setCurrentTime, getDuration,
            getAllQualities, getCurrentQuality, setCurrentQuality,
            getAllSubtitles, getCurrentSubtitle, setCurrentSubtitle,
            getAllAudios, getCurrentAudio, setCurrentAudio, setPlaybackAssetFormat, setPlaybackPreviewAssetFormat,
            showBackBtn, showBingeWatching, showBreadCrumbView, showElapsedTime, showForwardBtn,
            setForwardDuration, showMuteUnmuteBtn, showPlayPauseBtn, showQualitySelectorBtn,
            showSubTitleSelectorBtn, showFullscreenBtn,
            showRemainingTime, showRewindBtn, setRewindDuration, showSeekBar, showSeekPreview, showTitle, showTotalDuration,
            setPlayerTheme, setPlayerLoaded, setPlayerError, setPlayerLoading
        }
    });
    /* --------------------------------------------- */
    return (
        <PlayerWrapper ref={playerElementRef} {...{ theme }} className='player' id='player'>
            {isPlayerLoaded &&
                <PlayerSwitcher ref={playerSwitcherRef} {...{
                    defaultPlayer, smilUrl, baseUrl, mediaId, isLivePlayer,
                    onLoad: _onLoad, onBuffer: _onBuffer, onBufferComplete, onPlay: _onPlay,
                    onPause: _onPause, onCurrentTimeChange: _onCurrentTimeChange, onComplete: _onComplete,
                    onCrash: _onCrash, onConcurrencyLimitViolation: _onConcurrencyLimitViolation,
                    onGeoLocationBlocked: _onGeoLocationBlocked, onPlayerVpnBlocked: _onPlayerVpnBlocked,
                    playbackAssetFormat, playbackPreviewAssetFormat,
                    onLicenseNotGranted: _onLicenseNotGranted,
                    onUnknowFormat: _onUnknowFormat, seasonNumber,
                    castConfigPlaybackFormat, title,
                    onAccessDenied: _onAccessDenied, onUnload: _onUnload,
                    qualityMappingMode, qualityMapping, abrMode, type, debugModeEnabled, isShakaPlayerMount
                }} />
            }

            <PlayerUI ref={playerUIRef} {...{
                thisIsChannel,
                isPlayerLoaded,
                title, subtitle, thumbnailImgs,
                onPlayClick, onPauseClick, onMuteClick, onUnmuteClick,
                getAllQualities,
                isLivePlayer,
                getCurrentQuality, qualityMapping,
                setCurrentQuality,
                episodeNum, seasonNumber, seriesTitle,
                getAllSubtitles,
                getCurrentSubtitle,
                setCurrentSubtitle,
                setCurrentTime,
                onErrorOkClick,
                messages,
                mediaId, seriesId, type,
                uid,
                showPlaceholderPlayBtn,
                onPlaceholderPlayBtnClick,
                allowCasting,
                debugModeEnabled,
                playerElementRef,
                bingeCountdownDuration,
                isShakaPlayerMount,
                assetDetailsForRelatedRailInPlayer,
                catalogTheme
            }} />
        </PlayerWrapper>
    );
})

const PlayerWrapper = styled.div`
    video::-webkit-media-text-track-display {
        color:${({ theme: { body } }) => body && body.text.primary};
        font-size: 35px;
        font-family: "Open Sans", sans-serif, Helvetica, Arial;
}

  video::cue {
    opacity: .7;
    background-color: black;
  }

`
/* --------------------------------------------- */
Player.propTypes = {
    /* Default player for playback. */
    defaultPlayer: PropTypes.string,
    /* smilUrl for playback. */
    smilUrl: PropTypes.string,
    /* Title to be displayed in the player  */
    title: PropTypes.string,
    /* Subtitle to be displayed in the player  */
    subtitle: PropTypes.string,
    /* Array of thumbnail images to be displayed over the player  */
    thumbnailImgs: PropTypes.array,
    /* This callback gets triggered when player buffers.  */
    onBuffer: PropTypes.func,
    /* This callback gets triggered when player completes buffering.  */
    onBufferComplete: PropTypes.func,
    /* This callback gets triggered when playback plays.  */
    onPlay: PropTypes.func,
    /* This callback gets triggered when playback pauses.  */
    onPause: PropTypes.func,
    /* This callback gets triggered on every seek.  */
    onCurrentTimeChange: PropTypes.func,
    /* This callback gets triggered when playback completes.  */
    onComplete: PropTypes.func,
    /* This callback gets triggered when player crashes.  */
    onCrash: PropTypes.func,
    /* This callback gets triggered when recieved concurrency limit error.  */
    onConcurrencyLimitViolation: PropTypes.func,
    /* This callback gets triggered when recieved geolocation blocked error.  */
    onGeoLocationBlocked: PropTypes.func,
    /* This callback gets triggered when recieved LicenseNotGranted error.  */
    onLicenseNotGranted: PropTypes.func,
    /* This callback gets triggered when recieved unknow format error.  */
    onUnknowFormat: PropTypes.func,
    /* This callback gets triggered when recieved access denied error.  */
    onAccessDenied: PropTypes.func,
    /* This callback gets triggered when fullscreen state changes. */
    onFullscreenChange: PropTypes.func,
    /* On error popup ok click.  */
    onErrorOkClick: PropTypes.func,
    /* This callback gets triggered when subtitle text changes.  */
    // onSubtitleTextChange: PropTypes.func,
    /* This callback gets triggered when player is destroyed.  */
    onUnload: PropTypes.func,
    /* This callback gets triggered when subtitle is changed.  */
    onSubtitleChange: PropTypes.func,
    /* Object contains all messages related to player.  */
    messages: PropTypes.object
}
/* --------------------------------------------- */
Player.defaultProps = {
    defaultPlayer: 'Shaka',
    smilUrl: '',
    title: '',
    subtitle: '',
    thumbnailImgs: [],
    showPlaceholderPlayBtn: false,
    onBuffer: () => { },
    onBufferComplete: () => { },
    onPlay: () => { },
    onPause: () => { },
    onCurrentTimeChange: () => { },
    onComplete: () => { },
    onCrash: () => { },
    onConcurrencyLimitViolation: () => { },
    onGeoLocationBlocked: () => { },
    onLicenseNotGranted: () => { },
    onUnknowFormat: () => { },
    onAccessDenied: () => { },
    onFullscreenChange: () => { },
    onErrorOkClick: () => { },
    // onSubtitleTextChange: () => { },
    onUnload: () => { },
    onSubtitleChange: () => { },
    messages: {}
}
/* --------------------------------------------- */
export default Player;
/* --------------------------------------------- */